<template>
  <div :class="['sidebar', { 'sidebar--active': app.isMenuOpen }]">
    <div class="sidebar__header">
      <img alt="Madam Curious Admin" src="../assets/logo-extended.png" />
      <div class="sidebar__close">
        <b-button
          icon-left="close"
          outlined
          type="is-white"
          @click="closeMenuHandler"
        />
      </div>
    </div>
    <div class="sidebar__body">
      <div class="sidebar__links">
        <div
          v-for="(link, index) in links"
          :key="index"
          class="sidebar__link-container"
        >
          <router-link
            active-class="sidebar__link--active"
            class="sidebar__link has-text-light"
            exact-active-class="sidebar__link--exact-active"
            :to="link.to"
            @click.native="closeMenuHandler"
          >
            {{ link.label }}
          </router-link>
          <div
            v-if="link.links && link.links.length > 0"
            class="sidebar__nested-links"
          >
            <router-link
              v-for="(nestedLink, nIndex) in link.links"
              :key="`${index}/${nIndex}`"
              class="sidebar__link sidebar__nested-link has-text-light"
              exact-active-class="sidebar__link--exact-active"
              :to="nestedLink.to"
            >
              {{ nestedLink.label }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// TODO: Update this to use `buefy` menu components instead.
import { mapState } from 'vuex';

import con from '../store/mutationTypes';

const appLinks = [
  {
    to: '/home',
    label: 'Home',
    accessLevels: 'all'
  },
  {
    to: '/overall-analytics',
    label: 'Overall Analytics',
    accessLevels: ['admin']
  },
  {
    to: '/detailed-analytics',
    label: 'Detailed Analytics',
    accessLevels: ['admin']
  },
  {
    to: '/topics',
    label: 'Topics',
    accessLevels: ['admin', 'topicCreator']
  },
  {
    to: '/concepts',
    label: 'Concepts',
    accessLevels: ['admin', 'conceptCreator']
  },
  {
    to: '/flows',
    label: 'Chat Flows',
    accessLevels: ['admin', 'flowCreator']
  },
  {
    to: '/challenges',
    label: 'Challenges',
    accessLevels: ['admin', 'challengeCreator']
  },
  {
    to: '/groups',
    label: 'Groups',
    accessLevels: ['admin']
  },
  {
    to: '/daily_challenges',
    label: 'Daily Challenges',
    accessLevels: ['admin', 'DailyChallengeCreator']
  },
  {
    to: '/users',
    label: 'Users',
    accessLevels: ['admin', 'userCreator', 'userAnalyser']
  },
  {
    to: '/orders',
    label: 'Orders',
    accessLevels: ['admin', 'OrderCreator']
  },
  {
    to: '/notifications',
    label: 'Notifications',
    accessLevels: 'admin'
  },
  {
    to: '/settings',
    label: 'Settings',
    accessLevels: 'superUser'
  },
  {
    to: '/functions',
    label: '_unsafe_ Functions',
    accessLevels: 'superUser'
  },
  {
    to: '/admin',
    label: 'Manage Roles',
    accessLevels: 'superUser'
  }
];

export default {
  computed: {
    ...mapState(['app', 'auth']),
    isSuperUser() {
      return this.auth.claims.superUser;
    },
    links() {
      return appLinks.filter(({ accessLevels }) =>
        Array.isArray(accessLevels)
          ? accessLevels.some(this.hasAccessLevel)
          : this.hasAccessLevel(accessLevels)
      );
    }
  },
  methods: {
    closeMenuHandler() {
      this.$store.commit(con.UPDATE_APP_STATE_NAMESPACED, {
        isMenuOpen: false
      });
    },
    hasRole(role) {
      return this.isSuperUser || this.auth.claims[role];
    },
    hasAccessLevel(accessLevel) {
      return accessLevel === 'all' || this.hasRole(accessLevel);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~bulma/sass/utilities/mixins.sass';

.sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: fixed;
  left: 0;
  width: $sidebar-width;
  background-image: $app-gradient;
  overflow: hidden;
  transform: translateX(-100%) translate3d(0, 0, 0);
  transition: 180ms;
  z-index: $sidebar-mobile-z;

  &--active {
    transform: translateX(0%) translate3d(0, 0, 0);
  }

  &__header {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: $app-header-height;
    background-color: $dark-theme-color;
    box-shadow: $theme-background-shadow;
    margin-bottom: 0.5rem;

    img {
      height: $app-header-height - 1.2rem;
    }
  }

  &__close {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
  }

  &__body {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
  }

  &__links {
    display: flex;
    flex-direction: column;
  }

  &__link-container {
    margin: 0.1rem 0;
    margin-right: 0.75rem;
  }

  &__link {
    display: block;
    border-top-right-radius: 2rem;
    border-bottom-right-radius: 2rem;
    margin: 0.1rem 0;
    padding: 0.8rem;

    &:hover {
      background-color: rgba(1, 1, 1, 0.1);
    }

    &--active {
      background-color: rgba(1, 1, 1, 0.2) !important;
    }

    &--exact-active {
      background-color: rgba(1, 1, 1, 0.3) !important;
    }
  }

  &__nested-links {
    display: flex;
    flex-direction: column;
  }

  &__nested-link {
    padding-left: 2rem;
  }

  @include tablet {
    position: relative;
    transform: translateX(0%) translate3d(0, 0, 0);

    &__close {
      display: none;
    }
  }
}
</style>
